import React, { useEffect, useState } from 'react'
import { IconChevronRight, IconHome } from '@tabler/icons-react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import {
  Dialog,
  DialogOverlay,
  DialogPortal,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from 'src/components/atoms/dialog'
import Typography from 'src/components/atoms/Typography'
import Loader from 'src/components/atoms/Loader'
import { PrimaryButton } from 'src/components/atoms/buttons/PrimaryButton'
import { api } from 'src/api/root'
import FolderBreadcrumbs from 'src/components/molecules/FolderBreadcrumbs'
import FolderRow from 'src/components/molecules/FolderRow'
import { folderName } from 'src/enviroment/utils/folderName'

interface MoveToFolderModalProps {
  isOpen: boolean
  onClose: () => void
  onMoveSuccess: (targetFolderPath: string | null) => void
  projectId: string
  selectedIds: string[]
  currentFolderPath: string | null
}

const MoveToFolderModal: React.FC<MoveToFolderModalProps> = ({
  isOpen,
  onClose,
  onMoveSuccess,
  projectId,
  selectedIds,
  currentFolderPath,
}) => {
  const [browsePath, setBrowsePath] = useState<string | null>(null)
  const { mutateAsync: moveFiles, isPending: isMoving } = api.project.dataRoom.moveFiles.useMutation()
  const { data: foldersData, isLoading } = api.project.dataRoom.folders.useQuery(projectId, browsePath, undefined, {
    enabled: isOpen,
  })

  useEffect(() => {
    if (isOpen) {
      setBrowsePath(null)
    }
  }, [isOpen])

  const handleMoveHere = async () => {
    try {
      await moveFiles({ projectId, documentIds: selectedIds, folderPath: browsePath ?? undefined })
      onMoveSuccess(browsePath)
    } catch {
      // the mutation's onError already surfaces the failure
    }
  }

  const folders = foldersData?.data ?? []
  const fileLabel = selectedIds.length === 1 ? 'file' : 'files'
  const isAlreadyHere = browsePath === currentFolderPath

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogPortal>
        <DialogOverlay />
        <DialogPrimitive.Content
          aria-describedby={undefined}
          className="fixed left-[50%] top-[50%] z-[60] w-full max-w-[668px] translate-x-[-50%] translate-y-[-50%] rounded-lg border border-gray-500 bg-gray-900 p-7 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]"
        >
          <div className="flex items-start justify-between gap-10">
            <DialogHeader className="flex-1">
              <DialogTitle className="text-2xl font-bold leading-8 text-light-green-50">
                Move {selectedIds.length} {fileLabel} to folder
              </DialogTitle>
              <Typography variant="base" className="mt-2 text-gray-50">
                Pick a destination, or open a folder to go deeper
              </Typography>
            </DialogHeader>

            <DialogClose asChild>
              <button
                type="button"
                onClick={onClose}
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-gray-500 bg-gray-800 text-gray-50 transition-colors hover:border-light-green-500/60 hover:text-light-green-50"
              >
                <span className="sr-only">Close</span>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <path
                    d="M12 4L4 12M4 4L12 12"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </DialogClose>
          </div>

          <div className="mt-4">
            <FolderBreadcrumbs
              path={browsePath}
              rootLabel="All Files"
              rootIcon={<IconHome className="h-4 w-4 text-gray-200" />}
              variant="sm"
              onSelect={setBrowsePath}
            />
          </div>

          <div className="mt-4 flex max-h-[320px] min-h-[120px] flex-col gap-2 overflow-y-auto">
            {isLoading ? (
              <div className="flex flex-1 items-center justify-center">
                <Loader className="text-light-green-500" />
              </div>
            ) : folders.length === 0 ? (
              <div className="flex flex-1 items-center justify-center">
                <Typography variant="sm" className="text-gray-300">
                  No folders here.
                </Typography>
              </div>
            ) : (
              folders.map((folder) => (
                <FolderRow
                  key={folder.id}
                  name={folder.name}
                  disabled={isMoving}
                  onClick={() => setBrowsePath(folder.path)}
                  trailing={<IconChevronRight className="h-4 w-4 shrink-0 text-gray-500" />}
                />
              ))
            )}
          </div>

          <div className="mt-6 flex justify-end">
            <PrimaryButton onClick={handleMoveHere} disabled={isMoving || isAlreadyHere} isLoading={isMoving}>
              {browsePath ? `Move here: ${folderName(browsePath)}` : 'Move to All Files'}
            </PrimaryButton>
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  )
}

export default MoveToFolderModal
